import React, { useEffect, useState, useRef } from 'react';
import { webrtcService } from '../services/webrtcService';
import toast from 'react-hot-toast';
import { WebinarGrid } from '../components/WebinarGrid';
import fileShareService from '../services/fileShareService';
import transcriptionService from '../services/transcriptionService';
import CaptionDisplay from '../components/CaptionDisplay';
import TranscriptionSettings from '../components/TranscriptionSettings';
import pollService from '../services/pollService';
import PollDisplay from '../components/PollDisplay';
import PollModal from '../components/PollModal';
import Whiteboard from '../components/Whiteboard';
import BreakoutPanel from '../components/BreakoutPanel';
import WaitingRoomPanel from '../components/WaitingRoomPanel';
import WaitingRoomOverlay from '../components/WaitingRoomOverlay';
import RecordingNotice from '../components/RecordingNotice';
import { createMeetingRecorder, pickRecordingMimeType, recordingExtension } from '../utils/meetingRecorder';
import { Toaster } from 'react-hot-toast';
import whiteboardService from '../services/whiteboardService';
import muteService from '../services/muteService';
import { MdMic, MdMicOff, MdVideocam, MdVideocamOff, MdKeyboardArrowDown, MdSend, MdClose, MdChat, MdScreenShare, MdStopScreenShare, MdCircle, MdStop, MdLogout, MdPanTool, MdGroup, MdSettings, MdClosedCaption, MdClosedCaptionDisabled , MdBarChart, MdBrush } from 'react-icons/md';

export default function VideoConferencePage() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [email, setEmail] = useState('');

  // Token validation on mount (supports both JWT and one-time tokens)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    const roomParam = params.get('room');
    
    // If no token provided, redirect to vskul.com
    if (!token) {
      console.log('❌ No token provided - redirecting to vskul.com');
      window.location.href = 'https://vskul.com/';
      return;
    }
    
    if (token) {
      // Try JWT token first (instructors)
      try {
        const parts = token.split('.');
        if (parts.length === 3) {
          // JWT token detected
          const payload = JSON.parse(atob(parts[1]));
          
          // Check if JWT is expired
          const currentTime = Math.floor(Date.now() / 1000);
          if (payload.exp && payload.exp < currentTime) {
            console.log('❌ JWT token expired');
            if (payload.role === 'guest') {
              // Guests return through their invite link; no vskul.com redirect.
              return;
            }
            window.location.href = 'https://vskul.com/';
            return;
          }
          
          if (payload.role === 'guest') {
            // Guests have no account: use the name from the invite form.
            setEmail('');
            setUserName(payload.name || 'Guest');
          } else {
            setEmail(payload.email);
            setUserName(payload.name ? payload.name + ' (host)' : payload.email.split('@')[0]);
          }
          if (roomParam) setRoomId(roomParam);
          setIsLoggedIn(true);
          console.log('✅ Auto-login with JWT token:', payload.role === 'guest' ? payload.name : payload.email);
          // Will be called by useEffect when isLoggedIn changes
          return;
        }
      } catch (e) {
        // Not JWT, try one-time token
        console.log('❌ Invalid JWT format - trying one-time token');
      }
      
      // Validate one-time token (students)
      fetch('/api/validate-token', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token })
      })
      .then(res => res.json())
      .then(data => {
        if (data.valid) {
          setEmail(data.email);
          setUserName(data.email.split('@')[0]);
          setRoomId(roomParam || 'test-room-1');
          if (roomParam) setRoomId(roomParam);
          setIsLoggedIn(true);
          console.log('✅ Auto-login with one-time token:', data.email);
          // Will be called by useEffect when isLoggedIn changes
        } else {
          console.error('Token invalid:', data.error);
          // Token validation failed - redirect to vskul.com
          console.log('❌ Token validation failed - redirecting to vskul.com');
          window.location.href = 'https://vskul.com/';
        }
      })
      .catch(err => {
        console.error('Token validation error:', err);
        // Token validation error - redirect to vskul.com
        console.log('❌ Token validation error - redirecting to vskul.com');
        window.location.href = 'https://vskul.com/';
      });
    }
  }, []);

  const [token, setToken] = useState(new URLSearchParams(window.location.search).get('token') || '');
  const [password, setPassword] = useState('');
  const [userName, setUserName] = useState('');
  
  const localVideoRef = useRef(null);
  const remoteVideosRef = useRef({});
  const [isAudioEnabled, setIsAudioEnabled] = useState(false);
  const [isVideoEnabled, setIsVideoEnabled] = useState(false);
  const [participants, setParticipants] = useState({});
  const [reactions, setReactions] = useState({});
  const [remoteStreams, setRemoteStreams] = useState({});
  const isWebinar = (new URLSearchParams(window.location.search).get('layout') ?? 'webinar') === 'webinar';
  const peerConnectionsRef = useRef({});
  const [roomId, setRoomId] = useState('test-room-1'); // TODO: Make dynamic when room management is implemented
  const mySocketIdRef = useRef(null);
  const roomRef = useRef(roomId);     // room this browser is in now (changes for breakout rooms)
  const mainRoomRef = useRef(roomId); // the main room this session started in
  const conferenceInitializedRef = useRef(false);

  // Device selection
  const [audioDevices, setAudioDevices] = useState([]);
  const [videoDevices, setVideoDevices] = useState([]);
  const [selectedAudioDevice, setSelectedAudioDevice] = useState(null);
  const [selectedVideoDevice, setSelectedVideoDevice] = useState(null);
  const [showAudioMenu, setShowAudioMenu] = useState(false);
  const [showVideoMenu, setShowVideoMenu] = useState(false);

  // Chat
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [unreadChat, setUnreadChat] = useState(0);
    const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  // Waiting room (guest): '' | 'waiting' | 'denied' | 'full'. Waiting room (host): guests waiting to be let in.
  const [waitStatus, setWaitStatus] = useState('');
  const [waitingGuests, setWaitingGuests] = useState([]);
  // Set by the server from the meeting's token: this meeting is recorded and transcribed.
  const [recordRequired, setRecordRequired] = useState(false);
  const [recordPending, setRecordPending] = useState(false); // recording is due but the host must click to choose what to record
  const recordRequiredRef = useRef(false);
  const isHostRef = useRef(false);
  const isRecordingRef = useRef(false);
  const [isHandRaised, setIsHandRaised] = useState(false);
  const [showParticipants, setShowParticipants] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showTranscription, setShowTranscription] = useState(false);
  const [showBreakout, setShowBreakout] = useState(false);
  // Floating chat/participants windows (desktop): positions and stacking order
  const [panelPos, setPanelPos] = useState({ chat: null, participants: null });
  const [frontPanel, setFrontPanel] = useState('chat');
  const dragRef = useRef(null);
  useEffect(() => {
    const onMove = (e) => {
      const d = dragRef.current;
      if (!d) return;
      setPanelPos((p) => ({ ...p, [d.name]: { x: e.clientX - d.dx, y: e.clientY - d.dy } }));
    };
    const onUp = () => { dragRef.current = null; };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, []);
  const startPanelDrag = (name, e) => {
    if (window.innerWidth <= 768) return;
    const panel = e.currentTarget.closest('[data-floating-panel]');
    if (!panel) return;
    const rect = panel.getBoundingClientRect();
    dragRef.current = { name, dx: e.clientX - rect.left, dy: e.clientY - rect.top };
    setFrontPanel(name);
  };
  const floatStyle = (name, defaultRight) => {
    if (window.innerWidth <= 768) return { right: '0', top: '0' };
    const pos = panelPos[name];
    return pos ? { left: pos.x, top: pos.y, right: 'auto' } : { right: defaultRight, top: '0' };
  };
  // Collapse both boxes every time Settings opens
  useEffect(() => {
    if (showSettings) {
      setShowTranscription(false);
      setShowBreakout(false);
    }
  }, [showSettings]);
  const [sharedFiles, setSharedFiles] = useState([]);
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  // Latest participants and streams, read by the recorder, which keeps running after this render.
  const liveMeetingRef = useRef({ participants: {}, remoteStreams: {} });
  const [chatMessages, setChatMessages] = useState([]);
  useEffect(() => { if (isChatOpen) setUnreadChat(0); }, [isChatOpen, chatMessages]);
  const [chatInput, setChatInput] = useState('');
  const [chatTo, setChatTo] = useState('all');
  const chatMessagesEndRef = useRef(null);

  // Transcription state
  const [transcriptionActive, setTranscriptionActive] = useState(false);
  const [currentCaption, setCurrentCaption] = useState('');
  const [captionSpeaker, setCaptionSpeaker] = useState('');
  const [showCaptions, setShowCaptions] = useState(true); // Toggle captions visibility

  // Poll state
  const [currentPoll, setCurrentPoll] = useState(null);
  const [isHost, setIsHost] = useState(false); // Set from the signed instructor token
  const [isPollModalOpen, setIsPollModalOpen] = useState(false);
  const [isWhiteboardOpen, setIsWhiteboardOpen] = useState(false);
  useEffect(() => { whiteboardService.setOpen(isWhiteboardOpen); }, [isWhiteboardOpen]);

  // Microphone state of every participant (socket id -> muted), synced through muteService
  const [mutedPeers, setMutedPeers] = useState({});
  useEffect(() => muteService.subscribe(setMutedPeers), []);
  useEffect(() => {
    // The host muted this participant: turn our own microphone off
    muteService.setForceMuteHandler(() => {
      setIsAudioEnabled(false);
      webrtcService.toggleAudio(false);
      toast('The host muted you');
    });
  }, []);

  /**
   * Handle poll creation
   */
  const handleCreatePoll = (pollData) => {
    const poll = pollService.createPoll(
      pollData.question,
      pollData.options,
      isHost ? email.split('@')[0] : 'Guest'
    );
    if (poll) {
      console.log('🗳️ Poll created successfully', poll);
      // Set current poll immediately for the creator
      setCurrentPoll(poll);
    }
  };

  /**
   * Handle voting on poll
   */
  const handleVoteOnPoll = (optionId) => {
    if (!currentPoll) return;
    const voterId = email || 'anonymous';
    const success = pollService.vote(currentPoll.id, optionId, voterId);
    if (success) {
      console.log('🗳️ Vote recorded');
    }
  };

  /**
   * Handle poll closure
   */
  const handleClosePoll = () => {
    if (!currentPoll) return;
    pollService.closePoll(currentPoll.id);
    // Optionally hide the poll
    setTimeout(() => {
      setCurrentPoll(null);
    }, 1000);
  };

  const handleLogin = (e) => {
    e.preventDefault();
    setUserName(email.split('@')[0]);
    setIsLoggedIn(true);
    initializeConference();
  };

  const loadDevices = async () => {
    try {
      const devices = await webrtcService.getDevices();
      setAudioDevices(devices.audioInput);
      setVideoDevices(devices.videoInput);
      
      if (devices.audioInput.length > 0) {
        setSelectedAudioDevice(devices.audioInput[0].deviceId);
      }
      if (devices.videoInput.length > 0) {
        setSelectedVideoDevice(devices.videoInput[0].deviceId);
      }
    } catch (error) {
      console.error('Error loading devices:', error);
    }
  };

  const handleAudioDeviceChange = async (deviceId) => {
    setSelectedAudioDevice(deviceId);
    setShowAudioMenu(false);
    
    const success = await webrtcService.switchAudioDevice(deviceId);
    if (success) {
      toast.success('Audio device changed');
    } else {
      toast.error('Failed to change audio device');
    }
  };

  const handleVideoDeviceChange = async (deviceId) => {
    setSelectedVideoDevice(deviceId);
    setShowVideoMenu(false);
    
    const success = await webrtcService.switchVideoDevice(deviceId);
    if (success) {
      toast.success('Video device changed');
      if (localVideoRef.current && webrtcService.localStream) {
        localVideoRef.current.srcObject = webrtcService.localStream;
      }
    } else {
      toast.error('Failed to change video device');
    }
  };

  const sendChatMessage = () => {
    if (!chatInput.trim()) return;

    const socket = webrtcService.socket;
    socket.emit('send-message', {
      roomId,
      message: chatInput,
      to: chatTo === 'all' ? null : chatTo,
      senderName: userName,
      timestamp: new Date().toISOString()
    });

    console.log('Chat message sent:', chatInput);
    setChatInput('');
  };

  const scrollToBottom = () => {
    chatMessagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [chatMessages]);


  // Move this browser to another room (a breakout room, or back to the main room).
  // Closes the old peer connections, then joins the new room. The server's
  // user-joined events build the new connections.
  const switchRoom = (targetRoom, label) => {
    const socket = webrtcService.socket;
    if (!socket || !targetRoom || targetRoom === roomRef.current) return;
    Object.values(peerConnectionsRef.current).forEach((pc) => pc.close());
    peerConnectionsRef.current = {};
    setParticipants({});
    setRemoteStreams({});
    roomRef.current = targetRoom;
    setRoomId(targetRoom);
    socket.emit('join-room', { roomId: targetRoom, email, userName: userName || email.split('@')[0] });
    whiteboardService.attach(socket, targetRoom, userName || email.split('@')[0]);
    muteService.attach(socket, targetRoom);
    toast(`Now in ${label}`);
  };

  const registerSocketListeners = (socket) => {
    // Remove any existing listeners first
    socket.removeAllListeners('new-message');
    socket.removeAllListeners('user-joined');
    socket.removeAllListeners('user-left');
    socket.removeAllListeners('hand-raise');
    socket.removeAllListeners('participant-info');
    socket.removeAllListeners('receive-offer');
    socket.removeAllListeners('receive-answer');
    socket.removeAllListeners('receive-ice-candidate');

    // Chat listeners
    socket.on('new-message', (data) => {
      console.log('New message received:', data);
      if (data.senderName !== email.split('@')[0]) setUnreadChat(n => n + 1);
      setChatMessages(prev => [...prev, data]);
    });

    // User joined listener
    socket.on('user-joined', async (data) => {
      console.log('👥 User joined event received:', data);
      setParticipants(prev => {
        const updated = {
          ...prev,
          [data.userId]: { id: data.userId, stream: null, name: data.name || 'Unknown' }
        };
        console.log('📝 Participants state updated:', updated);
        return updated;
      });
      
      if (mySocketIdRef.current < data.userId) {
        console.log('I create offer (I joined first)');
        await createPeerConnection(data.userId);
      } else {
        console.log('I wait for offer (they joined first)');
        await createPeerConnection(data.userId, true);
      }
    });

    // User left listener
    socket.on('user-left', (data) => {
      console.log('User left:', data.userId);
      if (peerConnectionsRef.current[data.userId]) {
        peerConnectionsRef.current[data.userId].close();
        delete peerConnectionsRef.current[data.userId];
      }
      setParticipants(prev => {
        const updated = { ...prev };
        delete updated[data.userId];
        return updated;
      });
    });

    // Hand-raise listener
    socket.on('hand-raise', (data) => {
      console.log('🙋 Hand raise event RECEIVED:', data);
      setParticipants(prev => {
        // Only update if participant exists, don't create new entries
        if (prev[data.socketId]) {
          const updated = {
            ...prev,
            [data.socketId]: {
              ...prev[data.socketId],
              isHandRaised: data.action === 'raised'
            }
          };
          console.log('✋ Participants updated with hand raise:', updated);
          return updated;
        }
        console.log('⚠️ Hand raise from unknown participant:', data.socketId);
        return prev;
      });
      toast(`${data.senderName} ${data.action} hand`);
    });

    // Reaction listener: shows an emoji on the sender's tile for 3 seconds
    socket.on('reaction', (data) => {
      setReactions(prev => ({ ...prev, [data.socketId]: data.emoji }));
      setTimeout(() => {
        setReactions(prev => {
          const next = { ...prev };
          delete next[data.socketId];
          return next;
        });
      }, 3000);
    });

    // Participant info listener
    socket.on('participant-info', (data) => {
      console.log('Participant info received:', data);
      setParticipants(prev => ({
        ...prev,
        [data.userId]: {
          ...prev[data.userId],
          name: data.name
        }
      }));
    });

    // WebRTC offer listener
    socket.on('receive-offer', async (data) => {
      console.log('Received offer from:', data.from);
      await handleOffer(data.from, data.offer);
    });

    // WebRTC answer listener
    socket.on('receive-answer', async (data) => {
      console.log('Received answer from:', data.from);
      await handleAnswer(data.from, data.answer);
    });

    // ICE candidate listener
    socket.on('receive-ice-candidate', async (data) => {
      await handleIceCandidate(data.from, data.candidate);
    });

    // Note: File sharing now happens via WebRTC data channels (peer-to-peer)
    // Socket.io is no longer used for file transfer
    // Files are transferred directly between peers and auto-downloaded

    // File download ready listener
    socket.on('file-download-ready', (data) => {
      console.log('📥 File download ready:', data);
      
      // Find the file in sharedFiles to get base64 data
      const file = sharedFiles.find(f => f.fileId === data.fileId);
      if (file && file.base64) {
        console.log('📥 Reconstructing file from base64...');
        try {
          const binaryString = atob(file.base64);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }
          const blob = new Blob([bytes], { type: 'application/octet-stream' });
          const url = URL.createObjectURL(blob);
          
          const element = document.createElement('a');
          element.setAttribute('href', url);
          element.setAttribute('download', file.fileName);
          element.style.display = 'none';
          document.body.appendChild(element);
          element.click();
          document.body.removeChild(element);
          URL.revokeObjectURL(url);
          
          console.log('✅ File downloaded successfully');
        } catch (error) {
          console.error('❌ Error reconstructing file:', error);
          alert('Error downloading file');
        }
      } else {
        console.warn('⚠️ File data not found');
        alert('File data not available');
      }
    });

    // File download error listener
    socket.on('file-download-error', (data) => {
      console.log('❌ File download error:', data);
      alert(`Error downloading file: ${data.error}`);
    });

    // Poll socket listeners
    socket.on('poll-created', (pollData) => {
      console.log('🗳️ Poll created received:', pollData.question);
      pollService.receivePoll(pollData);
      setCurrentPoll(pollData);
    });

    socket.on('vote-recorded', (data) => {
      console.log('🗳️ Vote recorded:', data);
      if (data.poll) {
        pollService.receivePoll(data.poll);
        setCurrentPoll(data.poll);
      }
    });

    socket.on('poll-closed', (data) => {
      console.log('🗳️ Poll closed');
      if (currentPoll && currentPoll.id === data.pollId) {
        setCurrentPoll({ ...currentPoll, isClosed: true });
      }
    });

    socket.on('poll-deleted', (data) => {
      console.log('🗳️ Poll deleted');
      if (currentPoll && currentPoll.id === data.pollId) {
        setCurrentPoll(null);
      }
    });

    // Breakout rooms: the server moves us and tells us where to go
    socket.on('breakout-assigned', (d) => switchRoom(d.breakoutRoomId, d.label || 'breakout room'));
    socket.on('breakout-returned', (d) => switchRoom(d.roomId || mainRoomRef.current, 'main room'));
    socket.on('breakout-message', (d) => {
      toast(`${d.from}: ${d.message}`, { duration: 8000 });
    });

    // Whiteboard events are handled by whiteboardService.attach()
  };

  
  useEffect(() => {
    if (isLoggedIn && (email || userName) && !conferenceInitializedRef.current) {
      conferenceInitializedRef.current = true;
      initializeConference();
    }
  }, [isLoggedIn, email]);

const initializeConference = async () => {
    try {
      console.log('Initializing multi-user conference...');
      
      await loadDevices();
      
      const stream = await webrtcService.getLocalStream(selectedAudioDevice, selectedVideoDevice);
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
        
        // ✅ Disable audio and video tracks by default (muted on load)
        stream.getTracks().forEach(track => {
          if (track.kind === 'audio') track.enabled = false;
          if (track.kind === 'video') track.enabled = false;
        });
      }

      await webrtcService.connect(token, email);
      console.log('Connected to SFU');

      const socket = webrtcService.socket;
      mySocketIdRef.current = socket.id;
      
      // Chat listeners
      registerSocketListeners(socket);

      // Waiting room and meeting settings from the media server
      socket.on('waiting-room-status', (d) => {
        if (d.status === 'admitted') {
          setWaitStatus('');
          toast.success('The host let you in');
        } else {
          setWaitStatus(d.status);
        }
      });
      socket.on('waiting-room-list', (d) => setWaitingGuests(Array.isArray(d.waiting) ? d.waiting : []));
      socket.on('room-settings', (d) => setRecordRequired(!!d.record));

      // If the connection drops and comes back, the server has forgotten this browser's room.
      // Join again, restore host rights, and restart transcription and (if needed) recording.
      socket.io.on('reconnect', () => {
        const room = roomRef.current || roomId;
        socket.emit('join-room', { roomId: room, email, userName: userName || email.split('@')[0] });
        socket.emit('wb-identify', { token: new URLSearchParams(window.location.search).get('token'), roomId: room });
        socket.emit('waiting-room-request');
        transcriptionService.startListening();
        if (recordRequiredRef.current && isHostRef.current && !isRecordingRef.current) {
          startRecording();
        }
        toast('Reconnected to the meeting');
      });
      
      // Initialize transcription
      const userId = email || 'anonymous';
      transcriptionService.setSocket(socket);
      // Polls and votes go through the socket too. Without this, a poll only appears on the host's screen.
      pollService.setSocket(socket);
      transcriptionService.setContext(roomId, userId, email.split('@')[0]);
      transcriptionService.startListening();
      console.log('🎤 Transcription auto-started on room join');
      
      // Listen to caption updates
      const handleCaptionUpdate = (data) => {
        setCurrentCaption(data.text);
        setCaptionSpeaker(data.speaker || userName || email.split('@')[0]);
      };
      transcriptionService.on('caption-update', handleCaptionUpdate);
      
      socket.emit('join-room', { roomId, email, userName: userName || email.split('@')[0] });
      roomRef.current = roomId;
      mainRoomRef.current = roomId;
      socket.on('wb-role', (d) => { setIsHost(!!d.isHost); whiteboardService.setHost(!!d.isHost); });
      socket.on('wb-state', (d) => setIsWhiteboardOpen(!!d.open));
      socket.emit('wb-identify', { token: new URLSearchParams(window.location.search).get('token'), roomId });
      whiteboardService.attach(socket, roomId, email.split('@')[0]);
      muteService.attach(socket, roomId);
      
    } catch (error) {
      console.error('Error initializing conference:', error);
      toast.error('Failed to initialize conference');
    }
  };

  // Ask the server for short-lived TURN logins (the password never reaches the browser)
  const fetchIceServers = () => new Promise((resolve) => {
    const s = webrtcService.socket;
    const stun = [{ urls: ['stun:stun.l.google.com:19302'] }, { urls: ['stun:stun1.l.google.com:19302'] }];
    if (!s) return resolve(stun);
    const timer = setTimeout(() => resolve(stun), 3000);
    s.emit('get-ice-servers', (res) => { clearTimeout(timer); resolve(res?.iceServers || stun); });
  });

  const createPeerConnection = async (peerId, isAnswerer = false) => {
    try {
      if (peerConnectionsRef.current[peerId]) {
        console.log('Peer connection already exists for', peerId);
        return;
      }

      const peerConnection = new RTCPeerConnection({
        iceServers: await fetchIceServers()
      });

      peerConnectionsRef.current[peerId] = peerConnection;
      
      // Setup data channel for file sharing
      if (!isAnswerer) {
        // Offerer creates the data channel
        fileShareService.createDataChannel(peerConnection, peerId);
      }
      
      // Handle incoming data channels
      peerConnection.ondatachannel = (event) => {
        fileShareService.handleDataChannel(event, peerId);
      };


      const localStream = localVideoRef.current?.srcObject;
      if (localStream) {
        localStream.getTracks().forEach(track => {
          peerConnection.addTrack(track, localStream);
        });
      }

      peerConnection.ontrack = (event) => {
        console.log('Received remote track:', event.track.kind);
        setParticipants(prev => ({
          ...prev,
          [peerId]: { ...prev[peerId], stream: event.streams[0] }
        }));
        setRemoteStreams(prev => ({ ...prev, [peerId]: event.streams[0] }));
        if (remoteVideosRef.current[peerId]) {
          remoteVideosRef.current[peerId].srcObject = event.streams[0];
        setParticipants(prev => ({ ...prev }));
        }
      };

      peerConnection.onicecandidate = (event) => {
        if (event.candidate) {
          webrtcService.socket.emit('send-ice-candidate', {
            roomId: roomRef.current,
            candidate: event.candidate,
            targetSocketId: peerId
          });
        }
      };

      peerConnection.onconnectionstatechange = () => {
        console.log(`Connection state with ${peerId}:`, peerConnection.connectionState);
      };

      if (!isAnswerer) {
        console.log('Creating offer for', peerId);
        const offer = await peerConnection.createOffer();
        await peerConnection.setLocalDescription(offer);
        webrtcService.socket.emit('send-offer', {
          roomId: roomRef.current,
          offer,
          targetSocketId: peerId
        });
      }

    } catch (error) {
      console.error('Error creating peer connection:', error);
    }
  };

  const handleOffer = async (peerId, offer) => {
    try {
      if (!peerConnectionsRef.current[peerId]) {
        await createPeerConnection(peerId, true);
      }

      const peerConnection = peerConnectionsRef.current[peerId];
      
      if (peerConnection.signalingState === 'stable') {
        await peerConnection.setRemoteDescription(new RTCSessionDescription(offer));

        const answer = await peerConnection.createAnswer();
        await peerConnection.setLocalDescription(answer);
        webrtcService.socket.emit('send-answer', {
          roomId: roomRef.current,
          answer,
          targetSocketId: peerId
        });
      }
    } catch (error) {
      console.error('Error handling offer:', error);
    }
  };

  const handleAnswer = async (peerId, answer) => {
    try {
      const peerConnection = peerConnectionsRef.current[peerId];
      if (peerConnection && peerConnection.signalingState === 'have-local-offer') {
        await peerConnection.setRemoteDescription(new RTCSessionDescription(answer));
      }
    } catch (error) {
      console.error('Error handling answer:', error);
    }
  };

  const handleIceCandidate = async (peerId, candidate) => {
    try {
      const peerConnection = peerConnectionsRef.current[peerId];
      if (peerConnection && candidate) {
        await peerConnection.addIceCandidate(new RTCIceCandidate(candidate)).catch(e => {
          console.warn('Failed to add ICE candidate:', e.message);
        });
      }
    } catch (error) {
      console.error('Error adding ICE candidate:', error);
    }
  };

  const toggleAudio = () => {
    setIsAudioEnabled(!isAudioEnabled);
    webrtcService.toggleAudio(!isAudioEnabled);
    muteService.setMuted(isAudioEnabled); // true when the mic is now off
  };

  const toggleVideo = () => {
    setIsVideoEnabled(!isVideoEnabled);
    webrtcService.toggleVideo(!isVideoEnabled);
  };

  const startScreenShare = async () => {
    try {
      const screenStream = await navigator.mediaDevices.getDisplayMedia({
        video: { cursor: 'always' },
        audio: false
      });
      const screenTrack = screenStream.getVideoTracks()[0];
      const localStream = localVideoRef.current?.srcObject;
      if (!localStream) return;
      
      Object.values(peerConnectionsRef.current).forEach(peerConnection => {
        const sender = peerConnection.getSenders().find(s => s.track?.kind === 'video');
        if (sender) {
          sender.replaceTrack(screenTrack);
        }
      });
      
      const screenStreamWithAudio = new MediaStream([
        screenTrack,
        ...localStream.getAudioTracks()
      ]);
      localVideoRef.current.srcObject = screenStreamWithAudio;
      
      screenTrack.onended = () => {
        stopScreenShare();
      };
      
      setIsScreenSharing(true);
      console.log('Screen sharing started');
    } catch (error) {
      console.error('Screen share error:', error);
    }
  };

  const stopScreenShare = async () => {
    try {
      const localStream = localVideoRef.current?.srcObject;
      if (!localStream) return;
      
      const cameraStream = await webrtcService.getLocalStream();
      const cameraTrack = cameraStream.getVideoTracks()[0];
      
      Object.values(peerConnectionsRef.current).forEach(peerConnection => {
        const sender = peerConnection.getSenders().find(s => s.track?.kind === 'video');
        if (sender) {
          sender.replaceTrack(cameraTrack);
        }
      });
      
      localVideoRef.current.srcObject = cameraStream;
      setIsScreenSharing(false);
      console.log('Screen sharing stopped');
    } catch (error) {
      console.error('Stop screen share error:', error);
    }
  };



  liveMeetingRef.current = { participants, remoteStreams };

  // Everyone in the room: the host's own camera or shared screen, then each guest's video.
  const getRecordingTiles = () => {
    const { participants: people, remoteStreams: streams } = liveMeetingRef.current;
    const tiles = [];
    const self = localVideoRef.current?.srcObject || webrtcService.localStream;
    if (self) tiles.push({ id: 'self', name: 'Host', stream: self });
    Object.entries(people).forEach(([id, p]) => {
      const s = streams[id];
      if (s) tiles.push({ id, name: p?.name || 'Participant', stream: s });
    });
    return tiles;
  };

  const startRecording = async () => {
    // Already recording: do nothing, so the reconnect and auto-record paths cannot start a second recorder.
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') return;

    // Record this browser tab. It shows the whole meeting: the grid, shared screens, polls and the whiteboard.
    // Browsers only allow the choice after a click, so an automatic start asks the host to click once.
    let displayTrack = null;
    try {
      const display = await navigator.mediaDevices.getDisplayMedia({
        video: { displaySurface: 'browser' },
        audio: false,
        preferCurrentTab: true,
        selfBrowserSurface: 'include',
      });
      displayTrack = display.getVideoTracks()[0];
    } catch (error) {
      console.warn('Recording needs the host to choose what to record:', error);
      setRecordPending(true);
      return;
    }
    setRecordPending(false);

    try {
      // Records the tab's picture plus the room's mixed audio (every participant), not just this camera.
      const compositor = createMeetingRecorder(getRecordingTiles, { videoTrack: displayTrack });
      if (displayTrack) displayTrack.onended = () => stopRecording();
      const mimeType = pickRecordingMimeType();
      const mediaRecorder = new MediaRecorder(
        compositor.stream,
        mimeType ? { mimeType, videoBitsPerSecond: 2500000 } : undefined
      );

      recordedChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          recordedChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        compositor.stop();
        const type = mimeType || 'video/webm';
        const blob = new Blob(recordedChunksRef.current, { type });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `recording-${Date.now()}.${recordingExtension(type)}`;
        a.click();
        URL.revokeObjectURL(url);
        
        // Play stop notification with delay
        setTimeout(() => {
          const stopUtterance = new SpeechSynthesisUtterance('Recording stopped');
          stopUtterance.rate = 1.0;
          stopUtterance.pitch = 1.0;
          stopUtterance.volume = 1.0;
          window.speechSynthesis.cancel(); // Clear any pending utterances
          window.speechSynthesis.speak(stopUtterance);
        }, 500);
      };

      // Hand over data every 5 seconds, so a crash or closed tab keeps most of the recording.
      mediaRecorder.start(5000);
      mediaRecorderRef.current = mediaRecorder;
      setIsRecording(true);
      
      // Play start notification with delay
      setTimeout(() => {
        const startUtterance = new SpeechSynthesisUtterance('This session is now being recorded');
        startUtterance.rate = 1.0;
        startUtterance.pitch = 1.0;
        startUtterance.volume = 1.0;
        window.speechSynthesis.cancel(); // Clear any pending utterances
        window.speechSynthesis.speak(startUtterance);
      }, 500);
      
      toast.success('Recording started');
      console.log('Recording started');
    } catch (error) {
      console.error('Recording error:', error);
    }
  };

  const stopRecording = () => {
    try {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
        setIsRecording(false);
        console.log('Recording stopped');
      }
    } catch (error) {
      console.error('Stop recording error:', error);
    }
  };



  const sendReaction = (emoji) => {
    webrtcService.socket.emit('reaction', {
      roomId,
      socketId: webrtcService.socket.id,
      emoji
    });
  };

  const toggleHandRaise = () => {
    setIsHandRaised(!isHandRaised);
    const action = !isHandRaised ? 'raised' : 'lowered';
    console.log('🙋 Emitting hand-raise:', { roomId, socketId: webrtcService.socket.id, action, senderName: email.split('@')[0] });
    webrtcService.socket.emit('hand-raise', {
      roomId,
      socketId: webrtcService.socket.id,
      action,
      senderName: email.split('@')[0]
    });
    toast.success(`Hand ${action}`);
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    console.log('📁 File selected:', { fileName: file.name, fileSize: file.size });

    const fileId = Date.now().toString();
    const reader = new FileReader();

    reader.onload = (e) => {
      const fileData = e.target.result;
      
      // Convert ArrayBuffer to Base64 in chunks to avoid stack overflow
      let binary = '';
      const bytes = new Uint8Array(fileData);
      const chunkSize = 8192;
      for (let i = 0; i < bytes.length; i += chunkSize) {
        const chunk = bytes.subarray(i, i + chunkSize);
        binary += String.fromCharCode.apply(null, chunk);
      }
      const base64Data = btoa(binary);
      
      console.log('📁 File read complete, transmitting...');

      // Add to shared files list
      const newFile = {
        fileId,
        fileName: file.name,
        fileSize: file.size,
        uploadedBy: email.split('@')[0],
        timestamp: new Date().toISOString(),
        base64: base64Data
      };
      
      setSharedFiles(prev => [...prev, newFile]);

      // Send file to all connected peers via data channels
      Object.keys(peerConnectionsRef.current).forEach(peerId => {
        console.log('📤 Sending file to peer:', peerId);
        fileShareService.sendFile(peerId, file);
      });

      console.log('✅ File shared to all peers');
    };

    reader.onerror = () => {
      console.error('❌ Error reading file');
      alert('Error reading file');
    };

    reader.readAsArrayBuffer(file);
    event.target.value = '';
  };

  const getParticipantsList = () => {
    const list = Object.entries(participants).map(([id, data]) => ({
      id,
      name: data.name || 'Unknown',
      isHandRaised: data.isHandRaised || false
    }));
    return list;
  };

  const endSession = () => {
    try {
      // Stop all tracks
      if (localVideoRef.current?.srcObject) {
        localVideoRef.current.srcObject.getTracks().forEach(track => track.stop());
      }
      
      // Close all peer connections
      Object.values(peerConnectionsRef.current).forEach(pc => pc.close());
      peerConnectionsRef.current = {};
      
      // Disconnect socket
      if (webrtcService.socket) {
        webrtcService.socket.disconnect();
      }
      
      // Reset state
      setParticipants({});
      setChatMessages([]);
      setIsLoggedIn(false);
      conferenceInitializedRef.current = false;
      
      toast.success('Session ended');
      console.log('Session ended - going back to previous page');
      
      // Go back to the previous page (where they came from)
      setTimeout(() => {
        window.history.back();
      }, 500);
    } catch (error) {
      console.error('End session error:', error);
      toast.error('Error ending session');
      // Still redirect on error
      window.location.href = 'https://vskul.com/';
    }
  };

  // Keep refs in step with state, for the reconnect handler set up when the socket connects.
  useEffect(() => { isHostRef.current = isHost; }, [isHost]);
  useEffect(() => { isRecordingRef.current = isRecording; }, [isRecording]);
  useEffect(() => { recordRequiredRef.current = recordRequired; }, [recordRequired]);

  // Host: ask the server for the waiting list once this browser is confirmed as host.
  useEffect(() => {
    if (isHost && webrtcService.socket) webrtcService.socket.emit('waiting-room-request');
  }, [isHost]);

  // Meeting set to record: the host's browser starts recording as soon as it is the host and the stream is ready.
  // Recording is saved on this computer, so only the host's browser records.
  useEffect(() => {
    if (recordRequired && isHost && !isRecording && localVideoRef.current?.srcObject) {
      startRecording();
    }
    // Runs once per change of the meeting setting or host status, not on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recordRequired, isHost]);

  const admitGuests = (socketId) => {
    webrtcService.socket?.emit('waiting-room-admit', { roomId: roomRef.current || roomId, socketId });
  };
  const denyGuest = (socketId) => {
    webrtcService.socket?.emit('waiting-room-deny', { roomId: roomRef.current || roomId, socketId });
  };

  if (!isLoggedIn) {
    return (
      <div style={{ width: '100%', height: '100vh', backgroundColor: '#000' }}>
      </div>
    );
  }

  return (
    <div style={{ width: '100%', height: '100vh', backgroundColor: '#000', display: 'flex', flexDirection: 'row' }}>
    <WaitingRoomOverlay status={waitStatus} />
    {isHost && (
      <WaitingRoomPanel
        waiting={waitingGuests}
        onAdmit={admitGuests}
        onDeny={denyGuest}
        onAdmitAll={() => admitGuests('all')}
      />
    )}
    {recordPending && isHost && !isRecording && (
      <button
        type="button"
        onClick={() => startRecording()}
        style={{ position: 'fixed', left: 12, bottom: 96, zIndex: 1600, background: '#c62828', color: '#fff', border: 'none', borderRadius: 8, padding: '12px 16px', fontWeight: 'bold', cursor: 'pointer', boxShadow: '0 4px 14px rgba(0,0,0,0.4)' }}
      >
        Click to start recording, then choose "This tab"
      </button>
    )}
    {recordRequired && <RecordingNotice isHost={isHost} isRecording={isRecording} />}
    <div style={{ flex: 1, minWidth: 0, height: '100vh', display: 'flex', flexDirection: 'column', position: 'relative' }}>
      {/* Caption Display - Real-time captions above video grid */}
      <CaptionDisplay 
        isActive={transcriptionActive}
        currentCaption={currentCaption}
        speaker={captionSpeaker}
        language="en-US"
        isVisible={showCaptions}
      />

      {/* Poll Display - Floating widget */}
      {currentPoll && (
        <PollDisplay
          poll={currentPoll}
          onVote={handleVoteOnPoll}
          onClose={handleClosePoll}
        />
      )}

      {/* Poll Modal */}
      <PollModal
        isOpen={isPollModalOpen}
        onClose={() => setIsPollModalOpen(false)}
        onCreatePoll={handleCreatePoll}
        userName={userName}
      />

      {/* Whiteboard */}
      <Whiteboard
        isOpen={isWhiteboardOpen}
        onClose={() => setIsWhiteboardOpen(false)}
        isHost={isHost}
        roomId={roomId}
      />
      
      {/* Hidden remote sinks: webinar view has no visible remote <video>, so streams attach here */}
      {isWebinar && Object.keys(participants).map((peerId) => (
        <video
          key={`sink-${peerId}`}
          ref={el => remoteVideosRef.current[peerId] = el}
          autoPlay
          playsInline
          style={{ display: 'none' }}
        />
      ))}
      <Toaster position="top-center" />
      {isWebinar && <video ref={localVideoRef} autoPlay playsInline muted style={{ display: 'none' }} />}
      {isWebinar ? (
        <div style={{ flex: 1, padding: '10px', overflow: 'auto' }}>
          <WebinarGrid
            title={roomId}
            participants={[
              { id: 'self', name: `${userName} (You)`, hue: 220, isHandRaised, reaction: reactions[webrtcService.socket?.id] },
              ...Object.entries(participants).map(([id, p]) => ({
                id,
                name: p.name || 'Participant',
                hue: (id.charCodeAt(0) * 37) % 360,
                isHandRaised: !!p.isHandRaised,
                muted: mutedPeers[id] !== false, // everyone joins muted until they report otherwise
                reaction: reactions[id],
              })),
            ]}
            getStream={(id) => (id === 'self' ? webrtcService.localStream : (remoteStreams[id] || null))}
            screenStream={isScreenSharing ? localVideoRef.current?.srcObject : null}
            selfMuted={!isAudioEnabled}
            isHost={isHost}
            onMutePeer={(id) => muteService.mutePeer(id)}
            onToggleMic={toggleAudio}
            isSharing={isScreenSharing}
            onShare={isScreenSharing ? stopScreenShare : startScreenShare}
            onRaiseHand={toggleHandRaise}
            onReact={sendReaction}
            onLeave={endSession}
            controls={[
              { title: isAudioEnabled ? 'Mute' : 'Unmute', icon: isAudioEnabled ? <MdMic /> : <MdMicOff />, onClick: toggleAudio },
              { title: 'Microphone source', icon: <MdKeyboardArrowDown />, onClick: () => setShowAudioMenu(!showAudioMenu), active: showAudioMenu },
              { title: isVideoEnabled ? 'Stop video' : 'Start video', icon: isVideoEnabled ? <MdVideocam /> : <MdVideocamOff />, onClick: toggleVideo },
              { title: 'Camera source', icon: <MdKeyboardArrowDown />, onClick: () => setShowVideoMenu(!showVideoMenu), active: showVideoMenu },
              { title: isScreenSharing ? 'Stop share' : 'Share screen', icon: isScreenSharing ? <MdStopScreenShare /> : <MdScreenShare />, onClick: isScreenSharing ? stopScreenShare : startScreenShare, active: isScreenSharing },
              { title: isRecording ? 'Stop recording' : 'Start recording', icon: isRecording ? <MdStop /> : <MdCircle />, onClick: isRecording ? stopRecording : startRecording, active: isRecording },
              { title: 'Chat', icon: <MdChat />, badge: !isChatOpen && unreadChat > 0 ? unreadChat : null, onClick: () => setIsChatOpen(!isChatOpen), active: isChatOpen },
              { title: `People (${Object.keys(participants).length + 1})`, icon: <MdGroup />, badge: Object.keys(participants).length + 1, onClick: () => setShowParticipants(!showParticipants), active: showParticipants },
              { title: isHandRaised ? 'Lower hand' : 'Raise hand', icon: <MdPanTool />, onClick: toggleHandRaise, active: isHandRaised },
              { title: showCaptions ? 'Hide captions' : 'Show captions', icon: showCaptions ? <MdClosedCaption /> : <MdClosedCaptionDisabled />, onClick: () => setShowCaptions(!showCaptions), active: showCaptions },
              { title: 'Create poll', icon: <MdBarChart />, onClick: () => setIsPollModalOpen(true), active: isPollModalOpen },
              { title: 'Whiteboard', icon: <MdBrush />, onClick: () => setIsWhiteboardOpen(!isWhiteboardOpen), active: isWhiteboardOpen },
              { title: 'Settings', icon: <MdSettings />, onClick: () => setShowSettings(!showSettings), active: showSettings },
            ]}
            devicePanel={(showAudioMenu || showVideoMenu) ? (
              <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap', padding: '12px', background: '#1b2233', borderRadius: '10px', marginBottom: '10px' }}>
                {showAudioMenu && (
                  <div>
                    <div style={{ fontSize: '12px', color: '#aab' }}>Microphone</div>
                    {audioDevices.map(device => (
                      <button key={device.deviceId} type="button" onClick={() => handleAudioDeviceChange(device.deviceId)} style={{ display: 'block', margin: '4px 0', padding: '6px 10px', background: '#2d2d2d', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>
                        {device.label || 'Microphone'}
                      </button>
                    ))}
                  </div>
                )}
                {showVideoMenu && (
                  <div>
                    <div style={{ fontSize: '12px', color: '#aab' }}>Camera</div>
                    {videoDevices.map(device => (
                      <button key={device.deviceId} type="button" onClick={() => handleVideoDeviceChange(device.deviceId)} style={{ display: 'block', margin: '4px 0', padding: '6px 10px', background: '#2d2d2d', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>
                        {device.label || 'Camera'}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : null}
          />
        </div>
      ) : (
      <div style={{ flex: 1, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '10px', padding: '10px', overflow: 'auto' }}>
        <div style={{ backgroundColor: '#111', borderRadius: '8px', overflow: 'hidden', position: 'relative' }}>
          <video
            ref={localVideoRef}
            autoPlay
            playsInline
            muted
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
          <span style={{ position: 'absolute', bottom: '10px', left: '10px', color: '#fff', fontSize: '12px', backgroundColor: 'rgba(0,0,0,0.5)', padding: '5px 10px', borderRadius: '4px' }}>
            {userName} (You)
          </span>
        </div>

        {Object.entries(participants).map(([peerId, participant]) => (
          <div key={peerId} style={{ backgroundColor: '#111', borderRadius: '8px', overflow: 'hidden', position: 'relative' }}>
            <video
              ref={el => remoteVideosRef.current[peerId] = el}
              autoPlay
              playsInline
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
            {participant.isHandRaised && (
              <span style={{ position: 'absolute', top: '10px', right: '10px', fontSize: '36px', animation: 'pulse 2s infinite' }}>
                ✋
              </span>
            )}
            <span style={{ position: 'absolute', bottom: '10px', left: '10px', color: '#fff', fontSize: '12px', backgroundColor: 'rgba(0,0,0,0.5)', padding: '5px 10px', borderRadius: '4px' }}>
              {participant.name || 'Participant'}
            </span>
          </div>
        ))}
      </div>
      )}

      {/* Chat Panel */}
      {isChatOpen && (
        <div data-floating-panel="chat" onMouseDown={() => setFrontPanel('chat')} style={{
          position: 'fixed',
          ...floatStyle('chat', '0px'),
          width: window.innerWidth <= 768 ? '100%' : '300px',
          height: '60vh',
          backgroundColor: '#1a1a1a',
          borderLeft: '1px solid #333',
          display: 'flex',
          flexDirection: 'column',
          zIndex: frontPanel === 'chat' ? 1001 : 1000,
          border: '1px solid #666',
          borderRadius: '8px',
          boxShadow: '0 4px 16px rgba(0,0,0,0.6)',
          maxHeight: '60vh'
        }}>
          <div style={{
            padding: '15px',
            borderBottom: '1px solid #333',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <h3 onMouseDown={(e) => startPanelDrag('chat', e)} style={{ color: '#fff', margin: 0, fontSize: '16px', flex: 1, cursor: 'move', userSelect: 'none' }}>Chat</h3>
            <button
              onClick={() => setIsChatOpen(false)}
              style={{
                background: 'none',
                border: 'none',
                color: '#888',
                cursor: 'pointer',
                fontSize: '20px'
              }}
            >
              <MdClose />
            </button>
          </div>

          <div style={{
            flex: 1,
            overflowY: 'auto',
            padding: '15px',
            display: 'flex',
            flexDirection: 'column',
            gap: '10px'
          }}>
            {chatMessages.map((msg, idx) => (
              <div key={idx} style={{
                backgroundColor: msg.senderName === userName ? '#0d7377' : '#333',
                color: '#fff',
                padding: '8px 12px',
                borderRadius: '8px',
                maxWidth: '90%',
                wordWrap: 'break-word',
                marginLeft: msg.senderName === userName ? 'auto' : '0'
              }}>
                <div style={{ fontSize: '11px', opacity: 0.7, marginBottom: '4px' }}>
                  {msg.senderName}{msg.private ? (msg.senderId === webrtcService.socket?.id ? ' · Private (sent)' : ' · Private') : ''}
                </div>
                <div style={{ fontSize: '13px' }}>
                  {msg.message}
                </div>
              </div>
            ))}
            <div ref={chatMessagesEndRef} />
          </div>

          <div style={{ padding: '10px 15px 0' }}>
            <select
              value={chatTo}
              onChange={(e) => setChatTo(e.target.value)}
              style={{ width: '100%', padding: '6px', backgroundColor: '#2d2d2d', color: '#fff', border: '1px solid #444', borderRadius: '4px', fontSize: '12px' }}
            >
              <option value="all">To: Everyone</option>
              {Object.entries(participants).map(([id, pt]) => (
                <option key={id} value={id}>Private to: {pt.name || 'Participant'}</option>
              ))}
            </select>
          </div>
          <div style={{
            padding: '15px',
            borderTop: '1px solid #333',
            display: 'flex',
            gap: '8px'
          }}>
            <input
              type="text"
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && sendChatMessage()}
              placeholder="Type a message..."
              style={{
                flex: 1,
                padding: '8px',
                borderRadius: '4px',
                border: '1px solid #444',
                backgroundColor: '#222',
                color: '#fff',
                fontSize: '13px'
              }}
            />
            <button
              onClick={sendChatMessage}
              style={{
                padding: '8px 12px',
                backgroundColor: '#0d7377',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <MdSend />
            </button>
          </div>
        </div>
      )}

      {showParticipants && (
        <div data-floating-panel="participants" onMouseDown={() => setFrontPanel('participants')} style={{
          position: 'fixed',
          ...floatStyle('participants', '310px'),
          width: '300px',
          height: '60vh',
          backgroundColor: '#1a1a1a',
          borderLeft: '1px solid #333',
          display: 'flex',
          flexDirection: 'column',
          zIndex: frontPanel === 'participants' ? 1001 : 1000,
          border: '1px solid #666',
          borderRadius: '8px',
          boxShadow: '0 4px 16px rgba(0,0,0,0.6)'
        }}>
          <div style={{
            padding: '15px',
            borderBottom: '1px solid #333',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <h3 onMouseDown={(e) => startPanelDrag('participants', e)} style={{ color: '#fff', margin: 0, fontSize: '16px', flex: 1, cursor: 'move', userSelect: 'none' }}>
              Participants ({Object.keys(participants).length + 1})
            </h3>
            <button
              onClick={() => setShowParticipants(false)}
              style={{
                background: 'none',
                border: 'none',
                color: '#888',
                cursor: 'pointer',
                fontSize: '20px'
              }}
            >
              <MdClose />
            </button>
          </div>
          <div style={{
            flex: 1,
            overflowY: 'auto',
            padding: '15px',
            display: 'flex',
            flexDirection: 'column',
            gap: '10px'
          }}>
            <div style={{
              padding: '10px',
              backgroundColor: '#2d2d2d',
              borderRadius: '4px',
              color: '#fff',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <span>{email.split('@')[0]} (You)</span>
              {isHandRaised && <span style={{ color: '#ff9800' }}>✋</span>}
            </div>
            {Object.entries(participants).map(([id, data]) => (
              <div key={id} style={{
                padding: '10px',
                backgroundColor: '#2d2d2d',
                borderRadius: '4px',
                color: '#fff',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <span>{data.name || 'Unknown'}</span>
                {data.isHandRaised && <span style={{ color: '#ff9800' }}>✋</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Control Bar */}
      <div style={isWebinar ? { display: 'none' } : {
        backgroundColor: '#1a1a1a',
        padding: '12px',
        borderTop: '1px solid #333',
        display: 'flex',
        gap: '10px',
        alignItems: 'center',
        justifyContent: 'center',
        flexWrap: 'wrap',
        height: '50px'
      }}>
        {/* Audio */}
        <div style={{  display: 'flex', gap: '0', alignItems: 'center', position: 'relative' }}>
          <button
            onClick={toggleAudio}
            style={{
              padding: '8px 12px',
              backgroundColor: isAudioEnabled ? '#2d2d2d' : '#f44336',
              color: 'white',
              border: 'none',
              borderRadius: '4px 0 0 4px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '18px',
              height: '36px'
            }}
            title={isAudioEnabled ? 'Mute' : 'Unmute'}
          >
            {isAudioEnabled ? <MdMic /> : <MdMicOff />}
          </button>
          
          <button
            onClick={() => setShowAudioMenu(!showAudioMenu)}
            style={{
              padding: '8px 6px',
              backgroundColor: '#2d2d2d',
              color: '#888',
              border: 'none',
              borderRadius: '0 4px 4px 0',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '12px',
              borderLeft: '1px solid #444',
              height: '36px'
            }}
          >
            <MdKeyboardArrowDown size={16} />
          </button>

          {showAudioMenu && (
            <div style={{
              position: 'absolute',
              bottom: '100%',
              left: '0',
              backgroundColor: '#2d2d2d',
              border: '1px solid #444',
              borderRadius: '4px',
              marginBottom: '4px',
              minWidth: '200px',
              zIndex: 1000
            }}>
              {audioDevices.map(device => (
                <button
                  key={device.deviceId}
                  onClick={() => handleAudioDeviceChange(device.deviceId)}
                  style={{
                    display: 'block',
                    width: '100%',
                    padding: '8px 12px',
                    backgroundColor: selectedAudioDevice === device.deviceId ? '#444' : 'transparent',
                    color: '#fff',
                    border: 'none',
                    textAlign: 'left',
                    cursor: 'pointer',
                    fontSize: '13px',
                  }}
                >
                  {device.label || `Microphone ${device.deviceId.slice(0, 5)}`}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Video */}
        <div style={{ display: 'flex', gap: '0', alignItems: 'center', position: 'relative' }}>
          <button
            onClick={toggleVideo}
            style={{
              padding: '8px 12px',
              backgroundColor: isVideoEnabled ? '#2d2d2d' : '#f44336',
              color: 'white',
              border: 'none',
              borderRadius: '4px 0 0 4px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '18px',
              height: '36px'
            }}
            title={isVideoEnabled ? 'Stop Video' : 'Start Video'}
          >
            {isVideoEnabled ? <MdVideocam /> : <MdVideocamOff />}
          </button>
          
          <button
            onClick={() => setShowVideoMenu(!showVideoMenu)}
            style={{
              padding: '8px 6px',
              backgroundColor: '#2d2d2d',
              color: '#888',
              border: 'none',
              borderRadius: '0 4px 4px 0',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '12px',
              borderLeft: '1px solid #444',
              height: '36px'
            }}
          >
            <MdKeyboardArrowDown size={16} />
          </button>

          {showVideoMenu && (
            <div style={{
              position: 'absolute',
              bottom: '100%',
              left: '0',
              backgroundColor: '#2d2d2d',
              border: '1px solid #444',
              borderRadius: '4px',
              marginBottom: '4px',
              minWidth: '200px',
              zIndex: 1000
            }}>
              {videoDevices.map(device => (
                <button
                  key={device.deviceId}
                  onClick={() => handleVideoDeviceChange(device.deviceId)}
                  style={{
                    display: 'block',
                    width: '100%',
                    padding: '8px 12px',
                    backgroundColor: selectedVideoDevice === device.deviceId ? '#444' : 'transparent',
                    color: '#fff',
                    border: 'none',
                    textAlign: 'left',
                    cursor: 'pointer',
                    fontSize: '13px',
                  }}
                >
                  {device.label || `Camera ${device.deviceId.slice(0, 5)}`}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Screen Share */}
        <button
          onClick={isScreenSharing ? stopScreenShare : startScreenShare}
          style={{
            padding: "8px 12px",
            backgroundColor: isScreenSharing ? "#ff6b6b" : "#2d2d2d",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "18px",
            height: "36px"
          }}
          title={isScreenSharing ? "Stop Screen Share" : "Share Screen"}
        >
          {isScreenSharing ? <MdStopScreenShare /> : <MdScreenShare />}
        </button>

        {/* Recording */}
        <button
          onClick={isRecording ? stopRecording : startRecording}
          style={{
            padding: "8px 12px",
            backgroundColor: isRecording ? "#f44336" : "#2d2d2d",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "18px",
            height: "36px"
          }}
          title={isRecording ? "Stop Recording" : "Start Recording"}
        >
          {isRecording ? <MdStop /> : <MdCircle />}
        </button>

        {/* Chat */}
        <button
          onClick={() => setIsChatOpen(!isChatOpen)}
          style={{
            padding: '8px 12px',
            backgroundColor: isChatOpen ? '#0d7377' : '#2d2d2d',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '18px',
            height: '36px'
          }}
          title="Chat"
        >
          <MdChat />
        </button>

        {/* Participants */}
        <button
          onClick={() => setShowParticipants(!showParticipants)}
          style={{
            padding: "8px 12px",
            backgroundColor: showParticipants ? "#1976d2" : "#2d2d2d",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "18px",
            height: "36px",
            position: "relative"
          }}
          title="Participants List"
        >
          <MdGroup />
          <span style={{ marginLeft: "4px", fontSize: "12px" }}>({Object.keys(participants).length + 1})</span>
        </button>

        {/* Hand Raise */}
        <button
          onClick={toggleHandRaise}
          style={{
            padding: "8px 12px",
            backgroundColor: isHandRaised ? "#ff9800" : "#2d2d2d",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "18px",
            height: "36px"
          }}
          title={isHandRaised ? "Lower Hand" : "Raise Hand"}
        >
          <MdPanTool />
        </button>

        {/* Settings */}
        <button
          onClick={() => setShowSettings(!showSettings)}
          style={{
            padding: "8px 12px",
            backgroundColor: showSettings ? "#1976d2" : "#2d2d2d",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "18px",
            height: "36px"
          }}
          title="Settings"
        >
          <MdSettings />
        </button>
        {/* Caption Toggle */}
        <button
          onClick={() => setShowCaptions(!showCaptions)}
          style={{
            padding: "8px 12px",
            backgroundColor: showCaptions ? "#2d2d2d" : "#f44336",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "18px",
            height: "36px"
          }}
          title={showCaptions ? "Hide Captions" : "Show Captions"}
        >
          <span style={{ fontSize: "16px" }}>📝</span>
        </button>

        {/* Create Poll Button */}
        {isHost && (
          <button
            onClick={() => {
              console.log('🗳️ CREATE POLL BUTTON CLICKED');
              setIsPollModalOpen(true);
            }}
            style={{
              padding: "8px 12px",
              backgroundColor: "#4a90e2",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "18px",
              height: "36px"
            }}
            title="Create Poll"
          >
            <span style={{ fontSize: "16px" }}>🗳️</span>
          </button>
        )}

        {/* Whiteboard Button */}
        {isHost && (
          <button
            onClick={() => {
              console.log('🎨 WHITEBOARD BUTTON CLICKED');
              setIsWhiteboardOpen(!isWhiteboardOpen);
            }}
            style={{
              padding: "8px 12px",
              backgroundColor: isWhiteboardOpen ? "#7c3aed" : "#5b21b6",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "18px",
              height: "36px"
            }}
            title="Whiteboard"
          >
            <span style={{ fontSize: "16px" }}>🎨</span>
          </button>
        )}

        {/* End Session */}
        <button
          onClick={endSession}
          style={{
            padding: "8px 12px",
            backgroundColor: "#d32f2f",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "18px",
            height: "36px"
          }}
          title="End Session"
        >
          <MdLogout />
        </button>

      </div>
    </div>
      {/* Settings Panel */}
      {showSettings && (
        <div style={{
          position: window.innerWidth <= 768 ? 'fixed' : 'relative',
          flexShrink: 0,
          right: '0',
          top: '0',
          width: '350px',
          height: '100vh',
          backgroundColor: '#1a1a1a',
          borderLeft: '1px solid #333',
          display: 'flex',
          flexDirection: 'column',
          zIndex: 100,
          overflowY: 'auto'
        }}>
          <div style={{
            padding: '15px',
            borderBottom: '1px solid #333',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <h3 style={{ color: '#fff', margin: 0, fontSize: '16px' }}>Settings</h3>
            <button
              onClick={() => setShowSettings(false)}
              style={{
                background: 'none',
                border: 'none',
                color: '#888',
                cursor: 'pointer',
                fontSize: '20px'
              }}
            >
              <MdClose />
            </button>
          </div>
          
          <div style={{
            flex: 1,
            padding: '15px',
            display: 'flex',
            flexDirection: 'column',
            gap: '10px'
          }}>
            {/* Settings menu items will go here */}
            <div style={{ color: '#aaa', fontSize: '14px', marginTop: '10px', marginBottom: '15px', fontWeight: 'bold' }}>
              Advanced Features
            </div>

            {/* File Sharing Section */}
            <div style={{ marginBottom: '20px' }}>
              <div style={{ color: '#ccc', fontSize: '14px', marginBottom: '10px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span>📁</span>
                <span>File Sharing</span>
              </div>
              
              <label style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '15px',
                backgroundColor: '#222',
                border: '2px dashed #444',
                borderRadius: '4px',
                cursor: 'pointer',
                transition: 'all 0.2s',
                fontSize: '14px',
                color: '#ccc'
              }}>
                <span>+ Upload File</span>
                <input
                  type="file"
                  onChange={handleFileUpload}
                  style={{ display: 'none' }}
                  accept=".pdf,.doc,.docx,.txt,.ppt,.pptx,.xls,.xlsx,.jpg,.png,.gif"
                />
              </label>

              {sharedFiles.length > 0 && (
                <div style={{ marginTop: '12px' }}>
                  <div style={{ color: '#888', fontSize: '12px', marginBottom: '8px' }}>
                    Shared Files ({sharedFiles.length})
                  </div>
                  {sharedFiles.map((file) => (
                    <div
                      key={file.fileId}
                      style={{
                        padding: '12px',
                        backgroundColor: '#222',
                        borderRadius: '4px',
                        marginBottom: '8px',
                        fontSize: '13px',
                        color: '#ccc',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        transition: 'all 0.2s'
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <div style={{ color: '#fff', wordBreak: 'break-word' }}>📄 {file.fileName}</div>
                        <div style={{ color: '#888', fontSize: '11px', marginTop: '4px' }}>
                          by {file.uploadedBy} • {(file.fileSize / 1024).toFixed(2)} KB
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          console.log('📥 Download file:', file.fileId, file.fileName);
                          // Request file from server
                          webrtcService.socket.emit('file-download-request', {
                            roomId,
                            fileId: file.fileId,
                            fileName: file.fileName
                          });
                        }}
                        style={{
                          marginLeft: '10px',
                          padding: '6px 12px',
                          backgroundColor: '#1976d2',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '3px',
                          cursor: 'pointer',
                          fontSize: '12px',
                          whiteSpace: 'nowrap',
                          transition: 'all 0.2s'
                        }}
                        title="Download file"
                      >
                        ⬇️ Download
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Transcription Settings Component (collapsible) */}
            <div style={{ border: '1px solid #333', borderRadius: '6px', overflow: 'hidden' }}>
              <button
                type="button"
                onClick={() => setShowTranscription((v) => !v)}
                style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', background: '#222', color: '#ccc', border: 'none', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold' }}
              >
                <span>Transcription</span>
                <span>{showTranscription ? '▾' : '▸'}</span>
              </button>
              <div style={{ display: showTranscription ? 'block' : 'none' }}>
            <TranscriptionSettings 
              isActive={transcriptionActive}
              userName={userName}
              roomId={roomId}
              onStatusChange={(isActive) => setTranscriptionActive(isActive)}
            />
              </div>
            </div>

            {isHost && (
              <div style={{ border: '1px solid #333', borderRadius: '6px', overflow: 'hidden' }}>
                <button
                  type="button"
                  onClick={() => setShowBreakout((v) => !v)}
                  style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', background: '#222', color: '#ccc', border: 'none', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold' }}
                >
                  <span>Breakout Rooms</span>
                  <span>{showBreakout ? '\u25BE' : '\u25B8'}</span>
                </button>
                <div style={{ display: showBreakout ? 'block' : 'none' }}>
                  <BreakoutPanel
                    roomId={mainRoomRef.current || roomId}
                    participants={Object.entries(participants).map(([id, p]) => ({ id, name: p.name || 'Participant' }))}
                    getSocket={() => webrtcService.socket}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      )}

    </div>
  );
}

// Token validation on mount
